import {
  type AttachmentUploadDataDto,
  type AttachmentUploadDto,
  BusinessManagerRequester,
  type CmsBoletoDto,
  type CmsCompanyDto,
  type CmsMergeNfeBoletoDto,
  type CmsNfeDto,
  type CmsProductDto,
  type CmsTicketDto,
  type ConversationTicketDto,
  type DataBoletoDto,
  type DataNfeDto,
  type GetFaturaAccessDto,
  type MultipleContractDto,
  type SendContestationDto,
  type SendContestationResponseDto,
  type SendMessageThreadDto,
} from '@cobliteam/api-lib';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import useUser from '../useUser';

export const CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY = 'CRM_TICKETS_SCHEDULE_INSTALLATION';
export const CRM_SCHEDULING_SUMMARY_QUERY_KEY = 'CRM_SCHEDULING_SUMMARY_QUERY_KEY';

export const BUSINESS_MANAGER = 'BUSINESS_MANAGER';
export const BUSINESS_MANAGER_SCHEDULE_MULTIPLE_CONTRACTS_QUERY_KEY = 'SCHEDULE_MULTIPLE_CONTRACTS';
export const BUSINESS_MANAGER_SCHEDULE_BOLETO_DATA_QUERY_KEY = 'SCHEDULE_BOLETO_DATA';
export const BUSINESS_MANAGER_SCHEDULE_BOLETO_ATRASADOS_DATA_QUERY_KEY = 'SCHEDULE_BOLETO_ATRASADOS_DATA';
export const BUSINESS_MANAGER_SCHEDULE_COMPANY_BOLETO_DATA_QUERY_KEY = 'SCHEDULE_COMPANY_BOLETO_DATA';
export const BUSINESS_MANAGER_SCHEDULE_PRODUCT_DATA_QUERY_KEY = 'SCHEDULE_PRODUCT_DATA';
export const BUSINESS_MANAGER_SCHEDULE_NFE_DATA_QUERY_KEY = 'SCHEDULE_NFE_DATA';
export const BUSINESS_MANAGER_SCHEDULE_FATURA_ACCESS = 'SCHEDULE_FATURA_ACCESS';
export const BUSINESS_MANAGER_SCHEDULE_BOLETO_PDF_DATA_QUERY_KEY = 'SCHEDULE_BOLETO_PDF_DATA';
export const BUSINESS_MANAGER_SCHEDULE_TICKET_DATA_QUERY_KEY = 'SCHEDULE_TICKET_DATA';
export const BUSINESS_MANAGER_CONVERSATION_TICKET_DATA_QUERY_KEY = 'CONVERSATION_TICKET_DATA';
export const BUSINESS_MANAGER_UPLOAD_FILE_QUERY_KEY = 'UPLOAD_FILE';
export const BUSINESS_MANAGER_SEND_MESSAGE_QUERY_KEY = 'SEND_MESSAGE';
export const CRM = 'CRM';
export const CRM_SHIPPING_QUERY_KEY = 'TICKETS_SHIPPING';
export const CRM_SCHEDULING_QUERY_KEY = 'TICKETS_SCHEDULING';
export const CRM_DEVOLUTION_QUERY_KEY = 'TICKETS_DEVOLUTION';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getBusinessManagerRequester = () =>
  new BusinessManagerRequester({
    host: env.apiEfopBusinessManager,
  });

// Individual hooks - each with single responsibility
export const useMultipleContractMenu = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { getSession } = useUser();
  const fleetId: string = getSession().fleet.id;

  return useQuery<MultipleContractDto>({
    queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_MULTIPLE_CONTRACTS_QUERY_KEY],
    queryFn: () => getBusinessManagerRequester().getMultipleContracts(fleetId),
    enabled: !!enabled && !!fleetId,
  });
};

export const useBoletoTableData = (companyId: string) => {
  return useQuery<CmsBoletoDto>({
    queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_BOLETO_DATA_QUERY_KEY, companyId],
    queryFn: () => getBusinessManagerRequester().getBoletoTableData(companyId),
    enabled: !!companyId,
  });
};

export const useBoletoWithNfeWithCompanyData = (multipleContracts?: MultipleContractDto) => {
  const companyIds = multipleContracts?.data?.map((contract) => contract.companyId).filter(Boolean);
  const hasContracts = !!companyIds && companyIds.length > 0;

  return useQueries({
    queries:
      companyIds?.flatMap((companyId) => [
        {
          queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_BOLETO_DATA_QUERY_KEY, companyId],
          queryFn: () => getBusinessManagerRequester().getBoletoTableData(companyId),
          enabled: hasContracts,
        },
        {
          queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_NFE_DATA_QUERY_KEY, companyId],
          queryFn: () => getBusinessManagerRequester().getNfeTableData(companyId),
          enabled: hasContracts,
        },
      ]) ?? [],
    combine: (results) => {
      // Each company has 2 queries: boleto (even index) + nfe (odd index)
      const companiesCount = companyIds?.length ?? 0;
      const mergedData: CmsMergeNfeBoletoDto[] = [];

      const isLoading = !hasContracts || results.some((r) => r.isLoading);
      const isFetching = results.some((r) => r.isFetching);
      const isError = results.some((r) => r.isError);

      // Process each company's data
      for (let i = 0; i < companiesCount; i++) {
        const boletoIndex = i * 2; // 0, 2, 4, 6, 8  [boleto, nfe, boleto, nfe, boleto, nfe, boleto, nfe, boleto, nfe]
        const nfeIndex = i * 2 + 1; // 1, 3, 5, 7, 9 [nfe, boleto, nfe, boleto, nfe, boleto, nfe, boleto, nfe, boleto]

        const boletoResult = results[boletoIndex];
        const nfeResult = results[nfeIndex];

        if (boletoResult?.data && nfeResult?.data && multipleContracts) {
          const dataBoleto = boletoResult.data as CmsBoletoDto;
          const dataNfe = nfeResult.data as CmsNfeDto;

          if (dataBoleto?.status && dataNfe?.status) {
            const merged = mergeBoletoAndNfe(dataBoleto.data, dataNfe.data, multipleContracts);
            mergedData.push(...merged);
          }
        }
      }

      // Sort by emission date (newest first)
      if (mergedData.length) {
        mergedData.sort((a, b) => new Date(b?.data_emissao)?.getTime() - new Date(a?.data_emissao)?.getTime());
      }

      return { data: mergedData, isLoading, isFetching, isError };
    },
  });
};

// Helper function to merge boleto, nfe, and company data
function mergeBoletoAndNfe(boletoData: DataBoletoDto[] = [], nfeData: DataNfeDto[] = [], multipleContracts?: MultipleContractDto): CmsMergeNfeBoletoDto[] {
  return boletoData.map((boleto, idx) => {
    const companyData = multipleContracts?.data?.find((contract) => contract.companyId === `${boleto.id_company}`);
    const nfe = nfeData.find((n) => n.nr_boleto === boleto.nr_documento);
    const nfeInfo = nfe
      ? {
          nfeLink: nfe.link,
          nfeDataEmissao: nfe.data_emissao,
          nfeValor: nfe.title_value,
          nfeId: nfe.id_doc,
        }
      : {
          nfeLink: '',
          nfeDataEmissao: 'N/A',
          nfeValor: 'N/A',
          nfeId: '',
        };

    // Extract company info if available
    const companyInfo = companyData
      ? {
          razao_social: companyData.razao_social,
          cnpj: companyData.cnpj,
          date_contract: companyData.date_contract,
          address: companyData.address,
          phone: companyData.phone,
          sector: companyData.sector,
          responsible: companyData.responsible,
        }
      : {};

    return {
      ...boleto,
      ...nfeInfo,
      ...companyInfo,
      statusData: {
        statusCode: boleto.status,
        expireDate: boleto.data_vencimento,
      },
      boleto_link: {
        id_doc: boleto.id_doc,
        file_name: boleto.file_name,
      },
      key: idx + 1,
    };
  });
}

export const useCompanyData = (companyId: string) => {
  return useQuery<CmsCompanyDto>({
    queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_COMPANY_BOLETO_DATA_QUERY_KEY, companyId],
    queryFn: () => getBusinessManagerRequester().getCompanyData(companyId),
    enabled: !!companyId,
  });
};

export const useNfeTableData = (companyId: string) => {
  return useQuery<CmsNfeDto>({
    queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_NFE_DATA_QUERY_KEY, companyId],
    queryFn: () => getBusinessManagerRequester().getNfeTableData(companyId),
    enabled: !!companyId,
  });
};

export const useAccessFatura = (companyId: string) => {
  return useQuery<GetFaturaAccessDto>({
    queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_FATURA_ACCESS, companyId],
    queryFn: () => getBusinessManagerRequester().getAccessFatura(companyId),
    enabled: !!companyId,
  });
};

export const useTicketTableData = (companyId: string) => {
  return useQuery<CmsTicketDto>({
    queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_TICKET_DATA_QUERY_KEY, companyId],
    queryFn: () => getBusinessManagerRequester().getTicketTableData(companyId),
    enabled: !!companyId,
  });
};

export const useConversationTicketData = (ticketId: string, companyId?: string) => {
  return useQuery<ConversationTicketDto>({
    queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_CONVERSATION_TICKET_DATA_QUERY_KEY, ticketId],
    queryFn: () => getBusinessManagerRequester().getConversationTicketData(ticketId, companyId),
    enabled: !!ticketId,
  });
};

export const useProductData = (companyId: string) => {
  return useQuery<CmsProductDto>({
    queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_PRODUCT_DATA_QUERY_KEY, companyId],
    queryFn: () => getBusinessManagerRequester().getProductData(companyId),
    enabled: !!companyId,
  });
};

export const useUploadFileAttachment = () => {
  return useMutation<AttachmentUploadDataDto, unknown, AttachmentUploadDto>({
    mutationFn: async (file) => getBusinessManagerRequester().uploadFileAttachment(file),
    onError: () => {
      Alert.error('Falha ao fazer upload do arquivo.');
    },
  });
};

export const useSendMessageThread = () => {
  return useMutation<void, unknown, SendMessageThreadDto>({
    mutationFn: async (message) => getBusinessManagerRequester().sendMessageToThread(message),
    onError: () => {
      Alert.error('Falha ao enviar mensagem.');
    },
  });
};

export const useContestation = () => {
  return useMutation<SendContestationResponseDto, unknown, SendContestationDto>({
    mutationFn: async (contestation) => getBusinessManagerRequester().sendContestation(contestation),
    onError: () => {
      Alert.error('Falha ao enviar contestação.');
    },
  });
};

export const useGetBoletoPdf = () => {
  return useMutation({
    mutationFn: async (idDoc: string) => getBusinessManagerRequester().getBoletoPdfData(idDoc),
    onError: () => {
      Alert.error('Falha ao fazer baixar o arquivo.');
    },
  });
};

export const useGetFaturaPdf = () => {
  return useMutation({
    mutationFn: async (idDoc: string) => getBusinessManagerRequester().getFaturaDocument(idDoc),
    onError: () => {
      Alert.error('Falha ao fazer baixar o arquivo.');
    },
  });
};
